// TODO: remove with https://github.com/combinaut/sparkle/issues/32203 once form behaviors are owned by sp apps.
import { initializeCategorySelection } from "./category-selection";
import { initializeFormulaSuggestions } from "./formula-suggestions";
import { initializeQARoleMembership } from "./qa-role-membership";
import { initializeSingleSelectSubmission } from "./single-select-submission";

const states = new WeakMap();
const retiredStates = new WeakMap();

/**
 * Initializes formula suggestions, QA membership, category synchronization and select submission on forms within `root` and its closest owning form.
 * Form removal disposes these behaviors. Initialization completing after teardown is disposed immediately.
 * Reinitializing a disposed form waits for its pending initialization to settle.
 *
 * @param {ParentNode} root
 * @returns {Promise<void[]>} Resolves when initialization has completed for the owning forms.
 */
export function initializeFormBehaviors(root) {
  return Promise.all(owningForms(root).map(initializeOwnedForm));
}

async function initializeOwnedForm(form) {
  const retired = retiredStates.get(form);

  if (retired) {
    await retired;
  }

  if (
    !form.isConnected ||
    !form.querySelector(
      'sp-select, [data-behaviour~="formula-suggestions"], [data-behaviour~="qa-role-membership"], [id="main_category_select"]',
    )
  ) {
    return;
  }

  let state = states.get(form);

  if (!state) {
    state = { active: true, disposers: [], pending: new Set() };
    states.set(form, state);

    function disposeRemovedForm() {
      if (!form.isConnected) {
        disposeForm(form);
      }
    }

    state.observer = new MutationObserver(disposeRemovedForm);
    state.observer.observe(form.ownerDocument, { childList: true, subtree: true });
  }

  const pending = [];

  for (const initialize of [
    initializeFormulaSuggestions,
    initializeQARoleMembership,
    initializeCategorySelection,
    initializeSingleSelectSubmission,
  ]) {
    function retainDisposer(dispose) {
      if (!dispose) {
        return;
      }

      if (state.active) {
        state.disposers.push(dispose);
      } else {
        dispose();
      }
    }

    const initialization = Promise.resolve(initialize(form)).then(retainDisposer);
    state.pending.add(initialization);

    function forgetPendingInitialization() {
      state.pending.delete(initialization);
    }

    initialization.then(forgetPendingInitialization, forgetPendingInitialization);
    pending.push(initialization);
  }

  await Promise.all(pending);
}

/**
 * Disposes the owned form behaviors within `root` and its closest owning form.
 * Pending initialization is disposed when it completes.
 *
 * @param {ParentNode} root
 * @returns {void}
 */
export function disposeFormBehaviors(root) {
  owningForms(root).forEach(disposeForm);
}

function owningForms(root) {
  const forms = Array.from(root?.querySelectorAll("form") || []);
  const owner = root?.closest?.("form");

  if (owner) {
    forms.unshift(owner);
  }

  return forms;
}

function disposeForm(form) {
  const state = states.get(form);

  if (!state) {
    return;
  }

  state.active = false;
  state.observer.disconnect();
  states.delete(form);
  const retired = Promise.allSettled([...state.pending]);
  retiredStates.set(form, retired);

  function forgetRetiredState() {
    if (retiredStates.get(form) === retired) {
      retiredStates.delete(form);
    }
  }

  retired.then(forgetRetiredState);
  state.disposers.splice(0).forEach((dispose) => dispose());
}
